/**
 * Centralized runtime environment detection.
 *
 * The SPA ships as a SINGLE build served across every host (see
 * `config/apiConfig.ts`), so a build-time `import.meta.env` flag can't identify
 * the environment — detection is by **hostname** at runtime:
 *
 *   localhost / 127.0.0.1          → dev
 *   hostname matching stage|staging → stage
 *   anything else                  → production
 *
 * This module is the single source for that mapping and its presentation
 * (label, colours, tooltip). It was previously copy-pasted in three places —
 * `Layout`'s env pill, `Layout`'s favicon badge, and `VersionDisplay`'s label —
 * which is why it now lives here; all of them consume this.
 *
 * Semantics are preserved exactly from those three call sites: production
 * returns `isProduction: true` and a null `badge`, so callers that render
 * "nothing in prod" can keep doing `if (!badge) return null`.
 */

export type EnvironmentKey = 'dev' | 'stage' | 'production';

export interface EnvironmentBadge {
  /** Background colour for pills / favicon badge. */
  bg: string;
  /** Foreground (text) colour paired with `bg`. */
  fg: string;
  /** Short pill label, e.g. "Dev" / "Stage". */
  label: string;
  /** Compact label for the favicon overlay + version string, e.g. "DEV" / "STG". */
  shortLabel: string;
  /** Human-readable tooltip. */
  tooltip: string;
}

export interface EnvironmentInfo {
  key: EnvironmentKey;
  isProduction: boolean;
  /** Presentation for non-production environments; `null` in production. */
  badge: EnvironmentBadge | null;
}

const DEV_BADGE: EnvironmentBadge = {
  bg: '#f44336',
  fg: '#fff',
  label: 'Dev',
  shortLabel: 'DEV',
  tooltip: 'Local Development Environment',
};

const STAGE_BADGE: EnvironmentBadge = {
  bg: '#f9a825',
  fg: '#000',
  label: 'Stage',
  shortLabel: 'STG',
  tooltip: 'Staging Environment',
};

/**
 * Matches our real staging hosts (`office-stage.…`, `patient-stage.…`) and a
 * `staging.…`-style host.
 *
 * The three call sites this module replaced all used `hostname.includes('stage')`,
 * which does NOT match "staging" — 'stage' is not a substring of s-t-a-g-i-n-g.
 * Deployed hosts all use `-stage.`, so that worked in practice, but a host named
 * `staging.*` was classified as PRODUCTION: no env badge, and no non-production
 * login warning — the warning failing silently exactly where it's needed.
 *
 * Every misclassification this widening changes moves a host from "production"
 * (silent) to "non-production" (warned), which is the fail-safe direction: the
 * cost of a false "this isn't production" is a dismissible dialog, while the
 * cost of a false "this IS production" is someone trusting staging data.
 */
const STAGE_HOSTNAME = /stag(e|ing)/;

/** Resolve the current environment from the browser hostname. */
export const getEnvironment = (): EnvironmentInfo => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { key: 'dev', isProduction: false, badge: DEV_BADGE };
  }
  if (STAGE_HOSTNAME.test(hostname)) {
    return { key: 'stage', isProduction: false, badge: STAGE_BADGE };
  }
  return { key: 'production', isProduction: true, badge: null };
};

/** The hostname the classification was derived from (for display/diagnostics). */
export const getEnvironmentHostname = (): string =>
  typeof window !== 'undefined' ? window.location.hostname : '';

/**
 * Presentation for the current environment, or `null` in production.
 * Convenience for the "render nothing in prod" call sites.
 */
export const getEnvironmentBadge = (): EnvironmentBadge | null => getEnvironment().badge;

/** Full-word environment name for prose (dialog copy, tooltips). */
export const environmentDisplayName = (key: EnvironmentKey): string => {
  switch (key) {
    case 'dev':
      return 'Local Development';
    case 'stage':
      return 'Staging';
    default:
      return 'Production';
  }
};
